import { plainToClass } from "class-transformer";
import { MilestoneListingDto, MilestoneStatusListingDto } from "./milestones.objects";
import { TableColumnVisibilityController, TableColumnGroup } from "../../../shared/scripts/table-column-visibility";
import { Unique } from "../../../shared/scripts/array-functions";
import { IFilter } from "../../../shared/scripts/filtering/filtering.entities";
import { InitializeFilterDropdown, InitializeDateSelector, GetSelectedDate } from "../../../shared/scripts/filtering/filtering.utilities";
import { MilestoneDialog } from "../sites/scripts/site-milestone.dialog";
import { getData } from "../sites/scripts/site.utilities";
import { AuditHistoryDialog } from "../audit/scripts/audit-history.dialog";

export class MilestonesPage implements IFilter {
    milestones: MilestoneListingDto[];
    milestoneStatuses: MilestoneStatusListingDto[];
    dataTable: any;
    filterDropdownIds: string[];
    milestoneStartedDatePickerId: string = '#started-date-field';
    milestoneDatePickerId: string = '#expected-due-date-field';
    completionDatePickerId: string = '#completion-date-field';

    milestoneDialog: MilestoneDialog;

    isNonViewer: boolean;

    constructor(allMilestonesJson: any, allMilestoneStatusesJson: any, isNonViewer: boolean) {
        $(() => {
            this.isNonViewer = isNonViewer === true;
            this.milestones = plainToClass(MilestoneListingDto, <MilestoneListingDto[]>allMilestonesJson);
            this.milestoneStatuses = plainToClass(MilestoneStatusListingDto, <MilestoneStatusListingDto[]>allMilestoneStatusesJson);
            this.filterDropdownIds = [
                '#milestone-dropdown',
                '#study-dropdown',
                '#essential-dropdown',
                '#checklist-dropdown',
                '#status-dropdown',
                '#portal-dropdown'
            ];
            this.initializeFilters();
            this.initializeTable();

            const auditDialog = new AuditHistoryDialog();
            this.milestoneDialog = new MilestoneDialog(this.milestoneStatuses, !this.isNonViewer, auditDialog);
            this.milestoneDialog.successCallback = this.milestoneDialogSuccess.bind(this);

            $(document).on('click', '#milestones-table .ctms-table-col-actions .ctms-icon-pencil', async (evt) => {
                const row = $(evt.target).closest('tr');
                const t = $(evt.target).closest('table');
                const m = $(t).DataTable().row(row).data();

                this.milestoneDialog.resetDialog(m);
                $('#editMilestoneDialog').modal('show');
            });
        });
    }

    initializeFilters(): void {
        // Milestone Name filter
        let milestoneNames: string[] = this.milestones.map(m => m.MilestoneCategoryAndName);
        InitializeFilterDropdown('#milestone-dropdown', Unique(milestoneNames, true, ["None Selected"]), this);
        // Study filter
        let studyNames: string[] = this.milestones.map(m => m.StudyName);
        InitializeFilterDropdown('#study-dropdown', Unique(studyNames, true, ["None Selected"]), this);
        // Essential & Checklist filters
        //let yesNoOptions: string[] = ["Yes", "No"];
        let yesNoEssential: string[] = this.milestones.map(ai => ai.IsStartupEssential ? "Yes" : "No");
        InitializeFilterDropdown('#essential-dropdown', Unique(yesNoEssential, true, ["None Selected"]), this);
        let yesNoChecklist: string[] = this.milestones.map(ai => ai.IsChecklistItem ? "Yes" : "No");
        InitializeFilterDropdown('#checklist-dropdown', Unique(yesNoChecklist, true, ["None Selected"]), this);
        let yesNoPortal: string[] = this.milestones.map(ai => ai.ShowOnPortal ? "Yes" : "No");
        InitializeFilterDropdown('#portal-dropdown', Unique(yesNoPortal, true, ["None Selected"]), this);
        // Status filter
        //let statusNames: string[] = this.milestones.map(m => m.CTMSOperationMilestoneStatusName).filter(Boolean);
        let statusNames: string[] = this.milestoneStatuses.map(m => m.Name); 
        statusNames.push("Unassigned");
        InitializeFilterDropdown('#status-dropdown', Unique(statusNames, true, ["None Selected"]), this);
        // Expected Due Date filter
        //InitializeDateSelector('expected-due-date-field', this);
        // Completion Date filter
        //    InitializeDateSelector('completion-date-field', this);
        $(this.milestoneDatePickerId + ',' + this.milestoneStartedDatePickerId + ',' + this.completionDatePickerId).on('change input', () => {
            this.triggerFilter();
        });
    }

    initializeTable(): void {
        this.dataTable = $('#milestones-table').DataTable({
            dom: 'rtip',
            autoWidth: false,
            info: false,
            paging: false,
            search: true,
            scrollX: true,
            scrollY: '60vh',
            scrollCollapse: true,
            orderCellsTop: true,
            order: [[4, 'asc']], // Order by Expected Due Date
            columns: [
                { data: "SiteNumber", title: "Site Number" },
                { data: "SiteName", title: "Site Name" },
                { data: "MilestoneCategoryAndName", title: "Milestone" },
                { data: "StartedDate", title: "Started Date" },
                { data: "ExpectedDueDate", title: "Expected Due Date" },
                { data: "CompletionDate", title: "Completion Date" },
                { data: "CTMSOperationMilestoneStatusName", title: "Status" },
                {
                    data: null,
                    title: "",
                    className: "ctms-table-col-actions",
                    orderable: false,
                    render: function () {
                        return '<button><i class="ctms-icon-pencil"></i></button>';
                    }
                }
            ],
            data: this.milestones
        });

        $('#custom-dt-search').on('input', function () {
            $('#milestones-table').DataTable().search(String($(this).val() ?? '')).draw();
        });
        $('.ctms-icon-button[title="Clear Search"]').on('click', function () {
            $('#custom-dt-search').val('');
            $('#milestones-table').DataTable().search('').draw();
        });
    }

    updateTable(matchingItems: MilestoneListingDto[]): void {
        this.dataTable.clear();
        this.dataTable.rows.add(matchingItems).draw();
    }

    triggerFilter(): void {
        const selectedMilestone = this.getSelectedDropdownValues('#milestone-dropdown');
        const selectedStudy = this.getSelectedDropdownValues('#study-dropdown');
        const selectedStartupEssential = this.getSelectedDropdownValues('#essential-dropdown');
        const selectedChecklistItem = this.getSelectedDropdownValues('#checklist-dropdown');
        const selectedShowOnPortal = this.getSelectedDropdownValues('#portal-dropdown');
        const selectedStatus = this.getSelectedDropdownValues('#status-dropdown');
        let startedDateObj = $(this.milestoneStartedDatePickerId).val();
        let selectedStartedDate = "";
        if (Array.isArray(startedDateObj)) {
            selectedStartedDate = startedDateObj.length > 0 ? String(startedDateObj[0]) : "";
        } else {
            selectedStartedDate = String(startedDateObj);
        }
        // Get expected due date from datepicker as yyyy-MM-dd
        //const expectedDueDateObj = GetSelectedDate(this.milestoneDatePickerId);
        //let selectedExpectedDueDate = "";
        //if (expectedDueDateObj) {
        //    const yyyy = expectedDueDateObj.getFullYear();
        //    const mm = String(expectedDueDateObj.getMonth() + 1).padStart(2, '0');
        //    const dd = String(expectedDueDateObj.getDate()).padStart(2, '0');
        //    selectedExpectedDueDate = `${yyyy}-${mm}-${dd}`;
        //}
        let expectedDueDateObj = $(this.milestoneDatePickerId).val();
        let selectedExpectedDueDate = "";
        if (Array.isArray(expectedDueDateObj)) {
            selectedExpectedDueDate = expectedDueDateObj.length > 0 ? String(expectedDueDateObj[0]) : "";
        } else {
            selectedExpectedDueDate = String(expectedDueDateObj);
        }

        // Get completion date from datepicker as yyyy-MM-dd
        //const completionDateObj = GetSelectedDate(this.completionDatePickerId);
        //let selectedCompletionDate = "";
        //if (completionDateObj) {
        //    const yyyy = completionDateObj.getFullYear();
        //    const mm = String(completionDateObj.getMonth() + 1).padStart(2, '0');
        //    const dd = String(completionDateObj.getDate()).padStart(2, '0');
        //    selectedCompletionDate = `${yyyy}-${mm}-${dd}`;
        //}

        let completionDateObj = $(this.completionDatePickerId).val();
        let selectedCompletionDate = "";
        if (Array.isArray(completionDateObj)) {
            selectedCompletionDate = completionDateObj.length > 0 ? String(completionDateObj[0]) : "";
        } else {
            selectedCompletionDate = String(completionDateObj);
        }

        const matchingMilestones: MilestoneListingDto[] = [];

        this.milestones.forEach(item => {
            if (item.matchesFilter(
                selectedMilestone,
                selectedStartedDate,
                selectedExpectedDueDate,
                selectedCompletionDate,
                selectedStudy,
                selectedStartupEssential,
                selectedChecklistItem,
                selectedShowOnPortal,
                selectedStatus,
            )) {
                matchingMilestones.push(item);
            }
        });

        this.updateTable(matchingMilestones);
    }

    getSelectedDropdownValues(selectId: string): any {
        return $(selectId).val();
    }

    resetFilters(): void {
        this.filterDropdownIds.forEach((dropdownId: string) => {
            $(dropdownId).selectpicker('selectAll');
            $(dropdownId).selectpicker('refresh');
        });
        $(this.milestoneStartedDatePickerId).datepicker('update', '');
        $(this.milestoneDatePickerId).datepicker('update', '');
        $(this.completionDatePickerId).datepicker('update', '');
        $('#custom-dt-search').val('');
        this.dataTable.search('').draw();
        this.updateTable(this.milestones);
    }

    async milestoneDialogSuccess(response) {
        await this.reloadMilestones();
        this.triggerFilter();
    }

    async reloadMilestones() {
        const resp = await getData('/secure/ctms/milestones/milestones?handler=MilestonesList');
        if (resp && resp.allMilestonesJson) {
            this.milestones = plainToClass(MilestoneListingDto, <MilestoneListingDto[]>resp.allMilestonesJson);
            this.updateTable(this.milestones);
        }
    }
}